import type { BidType, BlastRecipientItem, PassReason, SessionUser } from '@motoveo/shared';
import { BID_TYPES, PASS_REASONS } from '@motoveo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { dollarsToCents, formatCents, formatDate, formatMiles } from '../lib/format';

const PRICED = new Set<BidType>(['cash', 'trade', 'cash_plus_trade']);

/** Operator bid-entry form for one recipient — logs/confirms a dealer bid (incl. no-bid/pass). */
function BidEntryForm({ recipientId, onDone }: { recipientId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [bidType, setBidType] = useState<BidType>('cash');
  const [amount, setAmount] = useState('');
  const [passReason, setPassReason] = useState<PassReason>('no_buyer');
  const [passText, setPassText] = useState('');
  const [conditions, setConditions] = useState('');
  const [source, setSource] = useState<'sms_operator_entered' | 'phone_logged' | 'email'>('sms_operator_entered');

  const priced = PRICED.has(bidType);
  const enter = useMutation({
    mutationFn: () =>
      api.bids.enter(recipientId, {
        bid_type: bidType,
        amount_cents: priced ? (dollarsToCents(amount) ?? undefined) : undefined,
        pass_reason: priced ? undefined : passReason,
        pass_reason_text: !priced && passText ? passText : undefined,
        conditions: conditions || undefined,
        source,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onDone();
    },
  });

  return (
    <div className="card" style={{ margin: '8px 0' }}>
      <div className="inline" style={{ flexWrap: 'wrap', gap: 8 }}>
        <label className="inline">
          Type
          <select value={bidType} onChange={(e) => setBidType(e.target.value as BidType)}>
            {BID_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        {priced ? (
          <label className="inline">
            Amount
            <input placeholder="$85,000" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
        ) : (
          <>
            <label className="inline">
              Pass reason
              <select value={passReason} onChange={(e) => setPassReason(e.target.value as PassReason)}>
                {PASS_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <input placeholder="pass note (optional)" value={passText} onChange={(e) => setPassText(e.target.value)} />
          </>
        )}
        <label className="inline">
          Source
          <select value={source} onChange={(e) => setSource(e.target.value as typeof source)}>
            <option value="sms_operator_entered">SMS (operator)</option>
            <option value="phone_logged">Phone</option>
            <option value="email">Email</option>
          </select>
        </label>
        <input placeholder="conditions (optional)" value={conditions} onChange={(e) => setConditions(e.target.value)} />
        <button onClick={() => enter.mutate()} disabled={enter.isPending || (priced && dollarsToCents(amount) == null)}>
          {enter.isPending ? 'Saving…' : 'Log bid'}
        </button>
        <button className="ghost" onClick={onDone} disabled={enter.isPending}>
          Cancel
        </button>
      </div>
      {enter.isError ? <p className="error">{(enter.error as Error).message}</p> : null}
    </div>
  );
}

function statusPill(status: string) {
  const good = status === 'sent' || status === 'delivered';
  const bad = status === 'consent_blocked' || status === 'suppressed' || status === 'nj_blocked' || status === 'failed';
  return <span className={`pill ${good ? 'conf-high' : bad ? 'conf-low' : 'conf-medium'}`}>{status}</span>;
}

function BidCell({ r }: { r: BlastRecipientItem }) {
  if (!r.bid) return <span className="muted">—</span>;
  if (r.bid.bid_type === 'no_bid' || r.bid.bid_type === 'pass') {
    return (
      <span className="warn" title={r.bid.pass_reason_text ?? ''}>
        pass · {r.bid.pass_reason ?? '—'}
      </span>
    );
  }
  return (
    <strong>
      {r.bid.bid_type}: {formatCents(r.bid.amount_cents)}
    </strong>
  );
}

export function BlastDetailPage({ user }: { user: SessionUser }) {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [entering, setEntering] = useState<string | null>(null);

  const blast = useQuery({ queryKey: ['blast', id], queryFn: () => api.blasts.get(id), enabled: Boolean(id) });

  const send = useMutation({
    mutationFn: () => api.blasts.send(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['blast', id] });
    },
  });

  const confirmSuggested = useMutation({
    mutationFn: (reviewTaskId: string) => api.bids.confirmSuggested(reviewTaskId, {}),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });

  return (
    <Layout user={user}>
      {blast.isLoading ? (
        <p className="muted">Loading…</p>
      ) : blast.isError ? (
        <p className="error">{(blast.error as Error).message}</p>
      ) : blast.data ? (
        (() => {
          const { blast: b, masked_packet: pkt, recipients, breakdown, suggested_bids } = blast.data;
          const canSend = b.status !== 'closed';
          return (
            <>
              <p>
                <Link to={`/submissions/${b.seller_submission_id}`} className="muted">
                  ← Submission
                </Link>
              </p>
              <div className="row-between">
                <h2>
                  Blast <span className="pill">{b.status}</span> <span className="muted">· {b.channel}</span>
                </h2>
                <div className="inline">
                  <button onClick={() => send.mutate()} disabled={send.isPending || !canSend}>
                    {send.isPending ? 'Sending…' : b.sent_at ? 'Re-send pending' : 'Send blast'}
                  </button>
                </div>
              </div>
              {send.isError ? <p className="error">{(send.error as Error).message}</p> : null}

              {/* THE MASKED PACKET — exactly what a dealer sees. Full VIN (transparency on the car);
                  seller PII masked (privacy on the person). */}
              <div className="card">
                <div className="row-between">
                  <h3>Masked packet — the dealer&apos;s-eye view</h3>
                  <span className="muted">no seller PII · full VIN visible</span>
                </div>
                <section className="grid-2">
                  <div className="field">
                    <span className="field-label">Vehicle</span>
                    <span className="field-value">
                      <strong>{pkt.model_designation ?? '—'}</strong> {pkt.model_year ?? ''} · {pkt.transmission}
                    </span>
                  </div>
                  <div className="field">
                    <span className="field-label">Exterior</span>
                    <span className="field-value">{pkt.paint_to_sample ? `PTS ${pkt.pts_color_name ?? ''}`.trim() : pkt.exterior_color ?? '—'}</span>
                  </div>
                  <div className="field">
                    <span className="field-label">Mileage</span>
                    <span className="field-value">{formatMiles(pkt.mileage)}</span>
                  </div>
                  <div className="field">
                    <span className="field-label">Region</span>
                    <span className="field-value">{pkt.region_label ?? '—'}</span>
                  </div>
                  <div className="field">
                    <span className="field-label">VIN</span>
                    <span className="field-value">{pkt.vin ? <code>{pkt.vin}</code> : '—'}</span>
                  </div>
                  <div className="field">
                    <span className="field-label">MSRP</span>
                    <span className="field-value">{formatCents(pkt.total_msrp_cents)}</span>
                  </div>
                </section>
                {pkt.highlight_options.length > 0 ? (
                  <p>
                    {pkt.highlight_options.map((o) => (
                      <span key={o.code} className="pill conf-high" title={o.description ?? ''}>
                        {o.description ?? o.code}
                      </span>
                    ))}
                  </p>
                ) : null}
                <p className="muted">Watermarked photos in packet: {pkt.watermarked_photo_keys.length}</p>
              </div>

              {/* the moat picture: asked → sent → bid / no-bid / ignored */}
              <div className="card">
                <h3>Response breakdown</h3>
                <p className="inline" style={{ flexWrap: 'wrap', gap: 10 }}>
                  <span><strong>{breakdown.total}</strong> asked</span>
                  <span className="muted">· {breakdown.sent + breakdown.delivered} sent</span>
                  <span className="muted">· {breakdown.window_deferred + breakdown.scheduled} deferred</span>
                  <span className="warn">· {breakdown.consent_blocked} consent-blocked · {breakdown.suppressed} suppressed · {breakdown.nj_blocked} NJ · {breakdown.skipped} skipped</span>
                  <span> · <strong>{breakdown.bid}</strong> bids · {breakdown.no_bid} no-bid · {breakdown.ignored} ignored</span>
                </p>
              </div>

              {suggested_bids.length > 0 ? (
                <div className="card">
                  <h3>Suggested bids (parsed from inbound SMS — confirm to record)</h3>
                  {suggested_bids.map((s) => (
                    <div key={s.review_task_id} className="row-between" style={{ borderTop: '1px solid #eee', padding: '6px 0' }}>
                      <span>
                        <em>&ldquo;{s.raw_body}&rdquo;</em> → {s.suggestion.ambiguous ? <span className="warn">ambiguous</span> : <strong>{s.suggestion.bid_type} {s.suggestion.amount_cents ? formatCents(s.suggestion.amount_cents) : ''}</strong>}{' '}
                        <span className="muted">({s.suggestion.confidence})</span>
                      </span>
                      <button
                        disabled={s.suggestion.ambiguous || confirmSuggested.isPending}
                        title={s.suggestion.ambiguous ? 'ambiguous — log via the recipient row instead' : 'confirm & record'}
                        onClick={() => confirmSuggested.mutate(s.review_task_id)}
                      >
                        Confirm
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="card">
                <h3>Recipients — everyone we asked ({recipients.length})</h3>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Dealer</th>
                        <th>State</th>
                        <th>Contact</th>
                        <th>Ch</th>
                        <th>Send status</th>
                        <th>Response</th>
                        <th>Bid</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipients.map((r) => (
                        <>
                          <tr key={r.id} className={r.has_named_buyer ? 'wedge-row' : ''}>
                            <td>
                              <Link to={`/dealers/${r.dealer_id}`}>{r.dealer_business_name}</Link>
                              {r.has_named_buyer ? <span className="pill conf-high">buyer</span> : null}
                            </td>
                            <td className="muted">{r.dealer_state}</td>
                            <td className="muted">{r.contact_name ?? r.contact_phone_e164 ?? r.contact_email ?? '—'}</td>
                            <td className="muted">{r.channel}</td>
                            <td>
                              {statusPill(r.send_status)}
                              {r.skip_reason ? <span className="muted" title={r.skip_reason}> ⓘ</span> : null}
                            </td>
                            <td>
                              {r.response_type === 'bid' ? <span className="ok-dot">● bid</span> : r.response_type === 'no_bid' ? <span className="warn">no-bid</span> : <span className="muted">ignored</span>}
                            </td>
                            <td>
                              <BidCell r={r} />
                            </td>
                            <td>
                              <button className="ghost" onClick={() => setEntering(entering === r.id ? null : r.id)}>
                                {entering === r.id ? 'Close' : r.bid ? 'Re-log' : 'Log bid'}
                              </button>
                            </td>
                          </tr>
                          {entering === r.id ? (
                            <tr key={`${r.id}-form`}>
                              <td colSpan={8}>
                                <BidEntryForm recipientId={r.id} onDone={() => setEntering(null)} />
                              </td>
                            </tr>
                          ) : null}
                        </>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="muted">Created {formatDate(b.created_at)}{b.sent_at ? ` · sent ${formatDate(b.sent_at)}` : ''}</p>
              </div>
            </>
          );
        })()
      ) : null}
    </Layout>
  );
}
